// Main-thread RPC client for crypto.worker.ts - the only thing allowed to
// talk to the worker. No crypto happens here, just request/response
// correlation and progress forwarding.
export type ProgressCallback = (processed: number, total: number) => void;

interface PendingEntry {
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
  onProgress?: ProgressCallback;
}

export class CryptoWorkerClient {
  private worker: Worker;
  private nextId = 1;
  private pending = new Map<number, PendingEntry>();

  constructor() {
    this.worker = new Worker(new URL("../workers/crypto.worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (ev: MessageEvent) => {
      const msg = ev.data;
      const entry = this.pending.get(msg.id);
      if (!entry) return;
      if (msg.type === "progress") {
        entry.onProgress?.(msg.processed, msg.total);
        return;
      }
      this.pending.delete(msg.id);
      if (msg.type === "error") {
        entry.reject(new Error(msg.message));
      } else {
        entry.resolve(msg.value);
      }
    };
  }

  private call<T>(payload: Record<string, unknown>, transfer: Transferable[] = [], onProgress?: ProgressCallback): Promise<T> {
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject, onProgress });
      this.worker.postMessage({ ...payload, id }, transfer);
    });
  }

  init(): Promise<{ ready: boolean }> {
    return this.call({ type: "init" });
  }

  generateAndWrapKeypair(passphrase: Uint8Array): Promise<{ publicKey: Uint8Array; wrapped: Uint8Array }> {
    return this.call({ type: "generateAndWrapKeypair", passphrase }, [passphrase.buffer]);
  }

  unlockPrivateKey(wrapped: Uint8Array, passphrase: Uint8Array): Promise<{ publicKey: Uint8Array }> {
    return this.call({ type: "unlockPrivateKey", wrapped, passphrase }, [passphrase.buffer]);
  }

  lockPrivateKey(): Promise<{ locked: boolean }> {
    return this.call({ type: "lockPrivateKey" });
  }

  // Switches which already-unlocked key subsequent decryptString calls
  // use - no passphrase needed, the private key never left the worker.
  setActiveKey(publicKey: Uint8Array): Promise<{ activated: boolean }> {
    return this.call({ type: "setActiveKey", publicKey });
  }

  encryptString(plaintext: Uint8Array, publicKey: Uint8Array): Promise<{ ciphertext: Uint8Array }> {
    return this.call({ type: "encryptString", plaintext, publicKey }, [plaintext.buffer]);
  }

  decryptString(ciphertext: Uint8Array): Promise<{ plaintext: Uint8Array }> {
    return this.call({ type: "decryptString", ciphertext });
  }

  encryptFile(
    file: Blob,
    passwords: Record<string, Uint8Array>,
    onProgress?: ProgressCallback,
  ): Promise<{ blob: Blob }> {
    return this.call({ type: "encryptFile", file, passwords }, [], onProgress);
  }

  decryptFile(file: Blob, password: Uint8Array, onProgress?: ProgressCallback): Promise<{ label: string; blob: Blob }> {
    return this.call({ type: "decryptFile", file, password }, [password.buffer], onProgress);
  }

  // Encrypts the file for one recipient's public key, returning the
  // container blob plus a small "envelope" (a random password sealed to
  // that key). Caller has to deliver both to the recipient somehow - this
  // app never sends either anywhere itself.
  encryptFileForRecipient(
    file: Blob,
    recipientPublicKey: Uint8Array,
    onProgress?: ProgressCallback,
  ): Promise<{ blob: Blob; envelope: Uint8Array }> {
    return this.call({ type: "encryptFileForRecipient", file, recipientPublicKey }, [], onProgress);
  }

  // Decrypts a file from encryptFileForRecipient using whichever key is
  // currently active to unseal the envelope - no separate password needed.
  decryptFileForRecipient(
    file: Blob,
    envelope: Uint8Array,
    onProgress?: ProgressCallback,
  ): Promise<{ blob: Blob }> {
    return this.call({ type: "decryptFileForRecipient", file, envelope }, [], onProgress);
  }

  terminate(): void {
    this.worker.terminate();
  }
}
